import { designLevel, hashSeed, Rng, type DesignSpec, type DesignedLevel } from "@enjoyment/core";
import { COUNTRIES, type Country } from "./countries";
import type { Card, CardKind } from "./game";

export interface GeneratedLevel {
  level: number;
  seed: number;
  pairCount: number;
  pairs: Country[];
  cards: Omit<Card, "matched" | "faceUp">[];
  cols: number;
  rows: number;
}

/**
 * Chapter design (levels 21+): the slot plays like ramp level `effective` in 12–21. Floor 12 is past every
 * introduction (12 pairs from 10, first Tier 3 countries at 12); the ramp's last step is level 21 (Tier 3 only), and
 * the deal stopped growing at level 10, so the slot's difficulty is spread over the two knobs the ramp has. Twists:
 * a mixed chapter (Tier 2 + Tier 3 as in levels 12–20, 12 pairs; a harder slot holds more Tier 3 countries) or a
 * far-lands chapter (Tier 3 only as from level 21; the deal size carries the slot: 8, 10 or 12 pairs). Counterparts
 * are never adjacent, as from level 4; countries and deal follow the real level number's seed.
 */
export const DESIGN: DesignSpec = { game: "baskent", floor: 12, ceiling: 21, twists: ["mixed", "far-lands"] };

interface DesignedDeal {
  pairs: number;
  /** Tier 3 countries in the deal; the rest are Tier 2. */
  tier3: number;
}

function rampPairCount(n: number): number {
  if (n === 1) return 3;
  if (n === 2) return 4;
  if (n === 3) return 5;
  if (n <= 5) return 6;
  if (n <= 7) return 8;
  if (n <= 9) return 10;
  return 12;
}

/** Deal of ramp level `d.effective` with the chapter's twist. */
function designedDeal(d: DesignedLevel): DesignedDeal {
  if (d.twist === "far-lands") {
    // The ramp's deal size nine levels back: 8 pairs for the easiest slots, 10 in the middle, 12 from effective 19.
    const pairs = rampPairCount(d.effective - 9);
    return { pairs, tier3: pairs };
  }
  // Tier 3 share between the ramp's Tier 2 pool (level 11) and its Tier 3 pool (level 21).
  const pairs = rampPairCount(d.effective);
  const share = Math.max(0, Math.min(1, (d.effective - 11) / 10));
  return { pairs, tier3: Math.round(pairs * share) };
}

export function pairCountFor(level: number): number {
  const n = Math.max(1, Math.floor(level));
  const d = designLevel(DESIGN, n);
  return d ? designedDeal(d).pairs : rampPairCount(n);
}

export function columnsFor(cardCount: number): number {
  if (cardCount <= 6) return 3;
  if (cardCount <= 8) return 4;
  if (cardCount <= 10) return 5;
  return 4;
}

function shuffleCopy<T>(items: readonly T[], rng: Rng): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Designed levels: the deal's Tier 3 and Tier 2 countries, drawn and ordered by the level's own seed. */
function designedCountries(n: number, deal: DesignedDeal): Country[] {
  const rng = new Rng(hashSeed(`baskent:${n}`));
  const far = shuffleCopy(COUNTRIES.filter((country) => country.tier === 3), rng).slice(0, deal.tier3);
  const known = shuffleCopy(COUNTRIES.filter((country) => country.tier === 2), rng).slice(0, deal.pairs - deal.tier3);
  return shuffleCopy([...far, ...known], rng);
}

export function countriesForLevel(level: number): Country[] {
  const n = Math.max(1, Math.floor(level));
  const d = designLevel(DESIGN, n);
  if (d) return designedCountries(n, designedDeal(d));
  if (n === 1) return COUNTRIES.filter((country) => country.tier === 1).slice(0, 3);
  if (n === 2) return COUNTRIES.filter((country) => country.tier === 1).slice(3, 7);
  const pairCount = rampPairCount(n);
  const pool = n <= 3
    ? COUNTRIES.filter((country) => country.tier === 1)
    : n <= 6
      ? COUNTRIES.filter((country) => country.tier <= 2)
      : n <= 11
        ? COUNTRIES.filter((country) => country.tier === 2)
        : n <= 20
          ? COUNTRIES.filter((country) => country.tier >= 2)
          : COUNTRIES.filter((country) => country.tier === 3);
  const rng = new Rng(hashSeed(`baskent:${n}`));
  const shuffled = pool.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled.slice(0, pairCount);
}

function isNearMatch(cards: readonly Omit<Card, "matched" | "faceUp">[], cols: number): boolean {
  const rows = Math.ceil(cards.length / cols);
  for (let i = 0; i < cards.length; i++) {
    const a = cards[i]!;
    const ar = Math.floor(i / cols);
    const ac = i % cols;
    for (let j = i + 1; j < cards.length; j++) {
      const b = cards[j]!;
      if (a.pairId !== b.pairId || a.kind === b.kind) continue;
      const br = Math.floor(j / cols);
      const bc = j % cols;
      if (Math.abs(ar - br) <= 1 && Math.abs(ac - bc) <= 1) return true;
    }
  }
  return rows < 0;
}

function shuffleCards(pairs: readonly Country[], cols: number, rng: Rng, avoidNeighbours: boolean): Omit<Card, "matched" | "faceUp">[] {
  const cards: Omit<Card, "matched" | "faceUp">[] = pairs.flatMap((country) => [
    { index: 0, kind: "country" as CardKind, pairId: country.id },
    { index: 0, kind: "capital" as CardKind, pairId: country.id },
  ]);
  const tryCount = avoidNeighbours ? 500 : 1;
  for (let attempt = 0; attempt < tryCount; attempt++) {
    const shuffled = cards.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = rng.int(i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
    const indexed = shuffled.map((card, index) => ({ ...card, index }));
    if (!avoidNeighbours || !isNearMatch(indexed, cols)) return indexed;
  }

  // Guaranteed-safe, still seeded layout: shuffle pair order, then place counterparts in halves.
  const order = pairs.slice();
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  const fallback: Omit<Card, "matched" | "faceUp">[] = [
    ...order.map((country) => ({ index: 0, kind: "country" as const, pairId: country.id })),
    ...order.map((country) => ({ index: 0, kind: "capital" as const, pairId: country.id })),
  ].map((card, index) => ({ ...card, index }));
  if (avoidNeighbours && isNearMatch(fallback, cols)) throw new Error("Could not create a spaced capital deal");
  return fallback;
}

/** Stable independent deal for every numbered level. No wall-clock or runtime randomness is used. */
export function generateLevel(level: number): GeneratedLevel {
  const n = Math.max(1, Math.floor(level));
  const seed = hashSeed(`baskent:${n}`);
  const rng = new Rng(seed);
  const pairCount = pairCountFor(n);
  const pairs = countriesForLevel(n).slice(0, pairCount);
  const cols = columnsFor(pairCount * 2);
  const cards = shuffleCards(pairs, cols, rng, n >= 4);
  return { level: n, seed, pairCount, pairs, cards, cols, rows: Math.ceil(cards.length / cols) };
}

export function hasAdjacentOwnCapital(deal: GeneratedLevel): boolean {
  return isNearMatch(deal.cards, deal.cols);
}
